import React from 'react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import loadable from '@wix/yoshi-flow-editor/loadable';
import useOnScreen from '@app/external/common/hooks/use-on-screen';
import { usePostPageSettings } from '@app/external/common/hooks/use-post-page-settings';
import { type NormalizedPost } from '@app/external/common/types';
import { useSelector } from '../../../common/components/runtime-context';
import {
  getCommentId,
  isSeo as getIsSeo,
} from '../../../common/store/basic-params/basic-params-selectors';
import type IPostPageCommentsSection from './post-page-comments-section';

const PostPageCommentsSection = loadable(
  () => import(/* webpackChunkName: "post-page-wix-comments" */ './post-page-comments-section'),
) as typeof IPostPageCommentsSection;

type Props = {
  post: NormalizedPost;
  forceComments: boolean;
  commentsRef: React.RefObject<HTMLDivElement>;
};

/*
  Deciding whether comments are needed has to happen *outside* the loadable module.

  `post-page-wix-comments` pulls in `@wix/comments-ooi-client`, whose chunk group owns ~218KB of
  CSS. While the section was mounted unconditionally, loadable resolved that chunk during SSR and
  the host inlined its CSS into the document head as render-blocking CSS - on every post page, for
  every visitor, even ones who never scroll down to the comments and even for posts with comments
  turned off. Keeping the gate eager means the chunk (and its CSS) is only requested once the
  comments are actually about to render.
*/
const PostPageCommentsSectionLoader: React.FC<Props> = (props) => {
  const { post, forceComments, commentsRef } = props;
  const isSeo = useSelector(getIsSeo);
  const commentId = useSelector(getCommentId);
  const { showComments } = usePostPageSettings();
  const { isSSR, isEditor } = useEnvironment();
  const { isOnScreen, ref } = useOnScreen<HTMLDivElement>(undefined, isEditor);

  const canSeePost = post?.canSeePaidContent !== false;
  const renderComments = Boolean(canSeePost && showComments && post?.id);

  if (!renderComments) {
    return null;
  }

  // SEO renders need the comments in the markup, and a comment deep link has to resolve
  // without waiting for the reader to scroll.
  const renderImmediately = isSeo || (!isSSR && Boolean(commentId));

  if (!renderImmediately && !isOnScreen && !forceComments) {
    return <div ref={ref} data-hook="comments-section-placeholder" />;
  }

  return <PostPageCommentsSection post={post} commentsRef={commentsRef} />;
};

export default PostPageCommentsSectionLoader;
